import React, { useMemo } from 'react';
import { Box, Grid, Card, CardContent, Typography, Button } from '@mui/material';
import { FolderOpen } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSchool } from '../contexts/SchoolContext';
import Spinner from '../components/Spinner';

const SchoolExamTicketClasses: React.FC = () => {
  const navigate = useNavigate();
  const { students, loading } = useSchool();

  const classSummaries = useMemo(() => {
    const counts: Record<string, number> = {};
    students.forEach((student) => {
      const className = (student.className || 'Unassigned').trim();
      counts[className] = (counts[className] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([className, studentCount]) => ({ className, studentCount }))
      .sort((a, b) => a.className.localeCompare(b.className));
  }, [students]);

  const handleOpenClass = (className: string) => {
    const encoded = encodeURIComponent(className);
    navigate(`/school/exam-ticket-settings/${encoded}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Exam Ticket Settings
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Select a class to configure its exam/hall ticket.
          </Typography>
        </Box>
      </Box>

      {classSummaries.length === 0 ? (
        <Box sx={{ p: 4, backgroundColor: 'background.paper', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            No class assignments found yet
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Add or assign classes to students under the Students page before configuring exam tickets.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/school/students')}>
            Go to Students
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {classSummaries.map((summary) => (
            <Grid item xs={12} sm={6} md={4} key={summary.className}>
              <Card sx={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column' }} onClick={() => handleOpenClass(summary.className)}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {summary.className}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {summary.studentCount} student{summary.studentCount !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
                <Box sx={{ p: 2, pt: 0 }}>
                  <Button fullWidth variant="outlined" endIcon={<FolderOpen />} onClick={(event) => { event.stopPropagation(); handleOpenClass(summary.className); }}>
                    Configure Class
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SchoolExamTicketClasses;
